import { ErrorMessage, Form, Formik } from "formik";
import { Bank as BankIcon, CheckCircle } from "@phosphor-icons/react";
import { FC, useMemo, useState } from "react";
import * as yup from "yup";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import useSaveBank from "../hooks/useSaveBank";
import PhoneInput from "./SearchablePhoneInput";
import BankListInput from "./BankListInput";
import useResolveBankAccount from "../hooks/useResolveBankAccount";
import { BankAccount } from "../@types";
import useBankList from "../hooks/useBankList";
import { useLocation, useNavigate } from "react-router-dom";
import { leaveSetupTask } from "../utils/setupTaskReturn";

const bankSchema = yup.object().shape({
  bank_code: yup.string().required("Bank code is required"),
  account_number: yup
    .string()
    .matches(/^\d{10}$/, "Account number must be exactly 10 digits")
    .required("Account number is required"),
  bank_account_phone_number: yup.string(),
});

const VerifyBank: FC<{}> = () => {
  const { refetch } = useAuth();
  const [foundUser, setFoundUser] = useState<BankAccount | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;

  const { bankListData, isLoading: isLoadingBankList } = useBankList();

  const {
    isVerifyingBankDetailsError,
    verifyBankDetails,
    verifyingBankDetails,
    verifyingBankDetailsError,
  } = useResolveBankAccount({
    onSuccess(data: any) {
      setFoundUser(data?.data);
      setReason(data?.data?.reason);
    },
  });

  const { isSavingBank, saveBank, isSaveBankError, saveBankError } =
    useSaveBank({
      onSuccess(data: any) {
        setReason(data?.data?.reason);
        if (data?.data?.reason) {
          toast.success("Something went wrong. Please try again.");
        } else {
          if (fromSetup) {
            leaveSetupTask(navigate, location.state);
          } else {
            navigate(-1);
          }
          toast.success("Bank details saved successfully");
        }
        refetch();
      },
      onError() {
        toast.error("Failed to verify bank account");
      },
    });

  const isLoading = useMemo(
    () => verifyingBankDetails || isLoadingBankList,
    [verifyingBankDetails, isLoadingBankList]
  );
  const isError = useMemo(
    () => isVerifyingBankDetailsError,
    [isVerifyingBankDetailsError]
  );
  const error = useMemo(
    () => verifyingBankDetailsError,
    [verifyingBankDetailsError]
  );

  return (
    <motion.div
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      className="fixed top-0 left-0 bg-deepBlue/80 backdrop-blur-sm w-full h-screen grid place-items-center p-5 z-50"
    >
      <Formik
        validationSchema={bankSchema}
        initialValues={{
          bank_code: "",
          account_number: "",
          bank_account_phone_number: "",
        }}
        onSubmit={(values) => {
          const selectedBank = bankListData?.data?.find(
            (bank) => bank.code === values.bank_code
          );

          if (foundUser) {
            saveBank(
              {
                bank_name: selectedBank?.name,
                bank_code: selectedBank?.code ?? values.bank_code,
                bank_account_number: foundUser?.account_number,
                bank_account_phone_number: values.bank_account_phone_number,
              },
              {}
            );
          } else {
            verifyBankDetails({
              bank_code: values.bank_code,
              account_number: values.account_number,
            });
          }
        }}
      >
        {({
          isValid,
          dirty,
          setFieldValue,
          values,
          handleChange,
          handleBlur,
        }) => (
          <Form className="bg-white max-w-lg w-full p-5 rounded-xl shadow-sm flex items-center flex-col gap-5">
            <div className="p-2 bg-gray-100 rounded-full ring-8 ring-gray-50">
              <BankIcon size={28} />
            </div>
            <div className="flex items-center flex-col gap-2">
              <div className="font-medium text-xl">Verify Bank Account</div>
              <p className="text-gray-500 text-center">
                Provide your bank and account number.
              </p>
            </div>

            <BankListInput
              bankListData={bankListData?.data}
              isLoading={isLoading}
              selected={values.bank_code}
              onSelect={(bank) => {
                setFoundUser(null);
                setFieldValue("bank_code", bank.code);
              }}
            />

            <div className="flex flex-col gap-2 w-full">
              <label
                htmlFor="account_number"
                className="text-sm font-bold text-gray-700"
              >
                Account Number
              </label>
              <input
                onChange={(e) => {
                  setFoundUser(null);
                  handleChange("account_number")(e);
                }}
                onBlur={handleBlur("account_number")}
                autoComplete="off"
                type="text"
                value={values.account_number}
                id="account_number"
                name="account_number"
                placeholder="Enter Account Number"
                className="px-4 py-3 bg-transparent rounded-lg border border-gray-300 shadow focus:outline-none w-full"
              />
              <ErrorMessage
                name="account_number"
                component={motion.div}
                className="text-red-500"
              />
              {foundUser && !reason && (
                <div className="flex items-center gap-1 text-sm">
                  <CheckCircle
                    weight="fill"
                    size={16}
                    className="text-deepBlue"
                  />
                  <div className="font-medium">{foundUser.account_name}</div>
                </div>
              )}
            </div>

            {(isError || reason) && (
              <div className="text-red-500">{error?.message || reason}</div>
            )}
            {foundUser && !reason && (
              <motion.div
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                className="flex flex-col w-full gap-2 text-left"
              >
                <div className="flex flex-col gap-2 w-full">
                  <label
                    htmlFor="bank_account_phone_number"
                    className="text-sm font-bold text-gray-700"
                  >
                    Bank Alert Phone number
                  </label>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="NG"
                    id="bank_account_phone_number"
                    name="bank_account_phone_number"
                    className="rounded-lg border border-gray-300 shadow px-4 w-full"
                    onChange={(phone) => {
                      setFieldValue(
                        "bank_account_phone_number",
                        phone?.toString()
                      );
                    }}
                  />
                  <ErrorMessage
                    name="bank_account_phone_number"
                    component={motion.div}
                    className="text-red-500"
                  />
                </div>
              </motion.div>
            )}
            {isSaveBankError && (
              <div className="text-red-500">{saveBankError?.message}</div>
            )}
            <div className="flex items-center gap-5 w-full">
              <button
                type="button"
                onClick={() => {
                  if (fromSetup) {
                    leaveSetupTask(navigate, location.state);
                  } else {
                    navigate(-1);
                  }
                }}
                className="p-3 px-5 flex-1 border-red-100 border-2 rounded-lg font-bold bg-red-50/50 text-red-500 text-sm"
              >
                Cancel
              </button>
              {!foundUser && (
                <button
                  type="submit"
                  disabled={isLoading || !isValid || !dirty}
                  className="p-3 px-5 flex-1 border-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {isLoading ? "Verifying" : "Verify"}
                </button>
              )}
              {foundUser && !reason && (
                <button
                  type="submit"
                  disabled={
                    isSavingBank ||
                    !values.bank_account_phone_number ||
                    !isValid ||
                    !dirty
                  }
                  className="p-3 px-5 flex-1 border-gray-700 border-2 rounded-lg font-bold bg-deepBlue text-white text-sm"
                >
                  {isSavingBank ? "Saving" : "Save"}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

export default VerifyBank;
